import React, { useEffect, Fragment, useCallback, useState, useRef } from 'react';
import { Button, Select, createUseStyles, Tooltip } from '@Sage/hoi-poi-ui';

import { getList, getValue } from 'lib/services/valueLists';
import { useWeb5Context } from 'lib/Web5Context';
import styles from './styles';
const useStyles = createUseStyles(styles, 'List');

const CACHE = {};

const formatList = (data) =>
    data.map((item) => ({
        ...item,
        label: item.description,
        value: item.id,
    }));

function List({
    schema,
    table,
    id,
    loadOptions,
    loadOptionsWithFuzzy,
    value,
    placeholder,
    noOptionsPlaceholder,
    customGetList,
    onClickButtonList,
    loadedValues,
    input,
    idFieldTable,
    valueObjectNeeded,
    isValueObject,
    ...props
}) {
    // id could be the id of the field, if comes from an schema
    // idFieldTable is the name of the field where the table comes from
    const { t, language, platform, implementation } = useWeb5Context();
    const classes = useStyles();
    const [valueObject, setValueObject] = useState();
    const listTable = table || schema?.context?.fields?.[id]?.table;
    const valueFromBackend = useRef(value && valueObjectNeeded);

    const getValueObjects = useCallback(() => {
        getValue(valueObjectNeeded, value, language)
            .then(({ data }) => {
                setValueObject({
                    value: data.id,
                    label:
                        data.description ||
                        data.name ||
                        data.model ||
                        data.strName ||
                        `${data?.firstName} ${data?.lastName}`,
                });
            })
            .catch((err) => console.error(err))
            .then(() => (valueFromBackend.current = false));
    }, [language, value, valueObjectNeeded]);

    useEffect(() => {
        if (valueObjectNeeded && valueFromBackend.current) {
            getValueObjects();
        }
    }, [getValueObjects, valueObjectNeeded]);

    useEffect(() => {
        if (!value && valueObject) setValueObject(null);
        if (value && valueObject && value !== valueObject.value) setValueObject(null);
    }, [value, valueObject]);

    // This loadedValues is necessary in order to include a new option when main options have been
    // already loaded (E.g. when creating a new Value List From ExtraFields Crud)
    const finalPropOptions = props.options || [];

    let loadedOptions =
        loadedValues?.length > 0 ? [...loadedValues, ...finalPropOptions] : finalPropOptions;

    const innerLoadOptions = useCallback(() => {
        const pr = new Promise((resolve, reject) => {
            if (customGetList) {
                customGetList(listTable, idFieldTable)
                    .then(({ data }) => {
                        resolve(formatList(data));
                    })
                    .catch((err) => {
                        console.error(err);
                        resolve([]);
                    });
            } else {
                if (platform === '101' && CACHE[listTable]) return resolve(CACHE[listTable] || []);
                if (platform === '107' && CACHE[implementation]?.[listTable])
                    return resolve(CACHE[implementation][listTable] || []);
                if (!listTable) return resolve([]);
                getList({ list: listTable, language })
                    .then(({ data }) => {
                        const formattedList = formatList(data);
                        if (platform === '101') CACHE[listTable] = formattedList;
                        if (platform === '107') {
                            if (!CACHE[implementation]) CACHE[implementation] = {};
                            CACHE[implementation][listTable] = formattedList;
                        }
                        resolve(formattedList);
                    })
                    .catch((err) => {
                        console.error(err);
                        resolve([]);
                    });
            }
        });
        return loadOptions ? loadOptions(pr) : pr;
    }, [loadOptions, customGetList, listTable, idFieldTable, platform, implementation, language]);

    const onClick = () => {
        onClickButtonList && onClickButtonList(value);
    };
    const finalValue = value !== undefined && value !== null ? value : undefined;
    const content = !value?.isEditable ? t('cfm_label_not_editable') : null;
    const loadOptionsWithTable = listTable ? innerLoadOptions : loadOptions;

    return (
        <Fragment>
            <Select
                id={id}
                labelMode="horizontal"
                value={valueObject || finalValue}
                isFullWidth={true}
                placeholder={placeholder || t('label_selectone')}
                {...props}
                options={loadedOptions}
                loadOptions={loadOptionsWithFuzzy || loadOptionsWithTable}
                noOptionsPlaceholder={
                    noOptionsPlaceholder ? t(noOptionsPlaceholder) : t('label_no_options')
                }
            />
            {onClickButtonList && (
                <div className={classes.button}>
                    <Tooltip content={content}>
                        <Button
                            isDisabled={!value?.isEditable}
                            size="small"
                            type="secondary"
                            onClick={onClick}
                        >
                            {t('cfm_label_update_table')}
                        </Button>
                    </Tooltip>
                </div>
            )}
        </Fragment>
    );
}

export default List;
